/**
 * AccountDotMatrix — W46 Custom SVG dot matrix.
 * One dot per account, colored by composite score band.
 * gray = unscored (D-098 composite_score=0 caveat)
 * Style: OPTION B Custom SVG. No Recharts.
 */

import React, { useState } from "react";

export interface AccountDot {
  id: string;
  name: string;
  composite: number;
  scoreBand: "unscored" | "green" | "yellow" | "orange" | "red";
  bucket: string;
  dealValue: number;
  daysSince: number;
  status: string;
}

interface AccountDotMatrixProps {
  accounts: AccountDot[];
}

const BAND_COLORS: Record<string, string> = {
  unscored: "#6B7280",
  green:    "#16A34A",
  yellow:   "#EAB308",
  orange:   "#EA580C",
  red:      "#DC2626",
};

function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

const DOT_R = 6;
const SPACING = 22;
const COLS = 16;

export function AccountDotMatrix({ accounts }: AccountDotMatrixProps) {
  const [hovered, setHovered] = useState<AccountDot | null>(null);

  if (!accounts || accounts.length === 0) {
    return <div className="w-full text-[12px] text-muted-foreground py-4 text-center">No accounts</div>;
  }

  const rows = Math.ceil(accounts.length / COLS);
  const svgW = COLS * SPACING + 8;
  const svgH = rows * SPACING + 8;

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {(["unscored", "green", "yellow", "orange", "red"] as const).map((band) => (
          <div key={band} className="flex items-center gap-1.5 text-[11px]">
            <span className="inline-block rounded-full flex-shrink-0"
              style={{ width: 10, height: 10, backgroundColor: BAND_COLORS[band] }} />
            <span className="text-muted-foreground capitalize">
              {band === "unscored" ? "Not scored"
               : band === "green" ? "Strong (80+)"
               : band === "yellow" ? "Watch (60-79)"
               : band === "orange" ? "Weak (40-59)"
               : "At risk (<40)"}
            </span>
          </div>
        ))}
      </div>
      <div className="relative overflow-x-auto">
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ maxHeight: 240 }}
          preserveAspectRatio="xMinYMin meet" role="img" aria-label="Account health dot matrix">
          {accounts.map((acc, idx) => {
            const col = idx % COLS;
            const row = Math.floor(idx / COLS);
            const cx = 4 + col * SPACING + DOT_R;
            const cy = 4 + row * SPACING + DOT_R;
            const color = BAND_COLORS[acc.scoreBand];
            const isHov = hovered?.id === acc.id;
            return (
              <circle key={acc.id} cx={cx} cy={cy} r={isHov ? DOT_R + 2 : DOT_R}
                fill={color} fillOpacity={acc.scoreBand === "unscored" ? 0.5 : 0.85}
                stroke={isHov ? "#fff" : "transparent"}
                strokeWidth={isHov ? 1.5 : 0}
                style={{ cursor: "pointer", transition: "r 0.1s" }}
                onMouseEnter={() => setHovered(acc)}
                onMouseLeave={() => setHovered(null)}>
                <title>
                  {acc.name}
                  {acc.composite > 0 ? ` \u2014 Score: ${acc.composite}` : " \u2014 Not scored"}
                  {` | ${acc.bucket}`}
                  {acc.dealValue > 0 ? ` | ${formatCurrency(acc.dealValue)}` : ""}
                  {acc.daysSince < 999 ? ` | ${acc.daysSince}d since touch` : ""}
                </title>
              </circle>
            );
          })}
        </svg>
        {hovered && (
          <div className="absolute bottom-full left-0 mb-1 bg-[hsl(var(--surface))] border border-border rounded-md p-2 text-[11px] shadow-md z-10 pointer-events-none max-w-[200px]">
            <p className="font-semibold text-foreground truncate">{hovered.name}</p>
            <p className="text-muted-foreground">
              {hovered.composite > 0 ? `Score: ${hovered.composite}` : "Not scored"}{" \u00b7 "}{hovered.bucket}
            </p>
            {hovered.dealValue > 0 && <p className="text-muted-foreground">{formatCurrency(hovered.dealValue)}</p>}
            {hovered.daysSince < 999 && <p className="text-muted-foreground">{hovered.daysSince}d since last touch</p>}
          </div>
        )}
      </div>
    </div>
  );
}
